/**
 * The loop, as one table.
 *
 * Five stages, and under each the readings that belong to it. This is the
 * diagram the sidebar draws, the routing table, the map the assistant moves the
 * panel by, and the list the i18n parity test walks — one table, because six
 * copies of "which screens exist and which step they belong to" is what this
 * file replaces, and they had already drifted: the assistant's screen list was
 * missing three readings, and the stage the server calls `analyse` was spelled
 * `analyze` here, with a lookup table and a ternary in the automation screen
 * patching between the two.
 *
 * A screen belongs to exactly one stage. If it does not, that is a question
 * about the screen rather than about this table.
 */

/**
 * A segment of the scope bar.
 *
 * The rule this type exists to enforce: a reading either honours a segment or
 * the bar does not draw it. A filter that is on screen and silently dropped at
 * the edge is worse than no filter at all — somebody sets it, believes the
 * numbers under it, and reads the wrong figure for a month.
 *
 * So these lists say what the *server* honours today, not what would be nice.
 * They widen as the endpoints learn a dimension, never before.
 */
export type Segment = "range" | "engine" | "locale" | "topics" | "intent" | "category" | "tag";

/** Every reading of the detection stage takes the same three, and most take topics. */
const DETECT = ["range", "engine", "locale"] as const;

export const LOOP = [
	{
		step: 1,
		key: "detect",
		// The one path that is not its stage's name. These readings shipped under
		// `/insights` a release ago, and moving a path a second time costs every
		// bookmark somebody made and buys a word.
		path: "insights",
		readings: [
			{ slug: "visibility", scope: [...DETECT, "topics"] },
			{ slug: "prompts", scope: [...DETECT, "topics", "intent"] },
			// Not part of the base loop: a reading of how an answer was *searched*
			// rather than of what it said. See `advanced` below.
			{ slug: "fanouts", scope: [...DETECT, "intent"], advanced: true },
			{ slug: "platforms", scope: DETECT },
			// Hidden until the region reading is implemented; restore the row, its
			// screen and its icon together.
			// { slug: "regions", scope: DETECT },
			{ slug: "personas", scope: DETECT },
			{ slug: "citations", scope: [...DETECT, "category", "tag"] },
		],
	},
	{
		step: 2,
		key: "analyse",
		path: "analyse",
		readings: [
			// No range: `citation_shares` is stored per week, and averaging four
			// of them would put a figure under a "30 days" label that is not the
			// thirty-day figure.
			{ slug: "competitors", scope: ["engine", "locale"] },
			{ slug: "gaps", scope: [...DETECT, "topics"] },
			{ slug: "sentiment", scope: DETECT },
			{ slug: "factcheck", scope: [...DETECT, "topics"] },
			// The report is written over a fixed thirty days and stored per
			// locale, so a range on this screen would name a window the figure
			// on it was not taken over.
			{ slug: "opportunities", scope: ["locale"], advanced: true },
		],
	},
	{
		step: 3,
		key: "generate",
		path: "generate",
		readings: [
			{ slug: "studio", scope: [] },
			{ slug: "plans", scope: [] },
			// The brief is assembled from the gaps in scope, so it takes the same
			// three the gaps screen does.
			{ slug: "briefs", scope: [...DETECT, "topics"] },
			// A draft is a decision, not a measurement: nothing on the list is
			// taken over a window, so there is no window to offer.
			{ slug: "drafts", scope: ["locale"] },
			{ slug: "templates", scope: ["locale"] },
		],
	},
	{
		step: 4,
		key: "publish",
		path: "publish",
		readings: [
			// The one figure here is how often the engines cited each page, and it
			// is taken over the window and the engine named. No locale: a page is
			// cited by answers in its own language, and narrowing by the bar's
			// would empty the Chinese rows on an English screen while leaving the
			// rows themselves in place.
			{ slug: "published", scope: ["range", "engine", "locale"] },
			{ slug: "distribution", scope: ["locale"] },
			{ slug: "crawlers", scope: ["range"] },
			// The embed language is independent of the interface language.
			{ slug: "badge", scope: ["locale"] },
		],
	},
	{
		step: 5,
		key: "reflow",
		path: "reflow",
		// No engine anywhere in this stage: a reflow re-asks the whole question
		// set on every engine the page was published against, so there is no
		// per-engine figure to narrow to.
		readings: [
			{ slug: "reflows", scope: ["range", "locale"] },
			// Not `pages`: the editor lives at `/w/:id/pages/:pageId` and the
			// drafts list answered to that word for a year, so a reading called
			// `pages` would be two different screens under one name — which is
			// exactly what the assistant's one-word screen vocabulary cannot
			// carry. The range is the window the "before" count is taken over,
			// so that it matches the window since publication.
			{ slug: "since", scope: ["range", "locale"] },
			// What the answers actually sent back. Everything above this line
			// measures what an engine said; these measure whether anybody then
			// arrived, which is the only figure in the loop a customer can put
			// in their own terms.
			//
			// No engine and no locale on any of them. A referral carries the
			// domain it was clicked from and nothing about the answer that
			// produced it, so an engine filter here would narrow the rows by a
			// dimension the rows are already cut on; and a visit has a
			// language, not a locale the questions were asked in.
			{ slug: "traffic", scope: ["range"] },
			{ slug: "journeys", scope: ["range"] },
			{ slug: "goals", scope: ["range"] },
			{ slug: "attribution", scope: ["range"] },
			{ slug: "vitals", scope: ["range"] },
			// Last because it is not a reading: it is the check somebody runs
			// in the minute after pasting the snippet into their site.
			{ slug: "live", scope: [] },
		],
	},
] as const satisfies readonly Stage[];

interface Stage {
	/** 1 to 5. The number the sidebar prints and `NextAction.stage` carries. */
	step: number;
	/** The stage's name, as the server, the marketing site and i18n all spell it. */
	key: string;
	/** The path all of this stage's readings live under. */
	path: string;
	/**
	 * `advanced` is the handful that are not part of the loop a brand is
	 * bought for — the fan-out reading and the opportunities digest. A plan
	 * either includes them or does not draw their rows at all; the server
	 * refuses both endpoints, which is the boundary.
	 */
	readings: readonly { slug: string; scope: readonly Segment[]; advanced?: true }[];
}

export type LoopStage = (typeof LOOP)[number];
type StageKey = LoopStage["key"];
/**
 * Every reading, by name.
 *
 * Slugs are unique across all five stages, which is what lets the assistant
 * name a screen in one word, lets `reading.<slug>` be one flat set of strings,
 * and lets an icon map or a component map be a `Record` the compiler checks.
 * A duplicate would quietly shadow, so it is also a test.
 */
export type ReadingSlug = LoopStage["readings"][number]["slug"];

/** Every reading, flat, in loop order. */
export const READINGS: readonly ReadingSlug[] = LOOP.flatMap((stage) => stage.readings.map((one) => one.slug));

/**
 * The readings a plan without them does not draw at all, from the table above
 * rather than written out again.
 *
 * A set rather than a field read off each row: `as const` gives every row its
 * own literal type, so `reading.advanced` does not exist on the twenty that
 * do not carry it, and every call site would need the narrowing.
 */
export const ADVANCED_READINGS: ReadonlySet<string> = new Set(
	LOOP.flatMap((stage) => stage.readings.filter((one) => "advanced" in one).map((one) => one.slug)),
);

export function stageOfReading(slug: string): LoopStage | undefined {
	return LOOP.find((stage) => stage.readings.some((reading) => reading.slug === slug));
}

/** Where a reading lives, under a brand. */
export function readingPath(base: string, slug: string): string {
	const stage = stageOfReading(slug);
	return stage ? `${base}/${stage.path}/${slug}` : base;
}

/**
 * The links between readings that the numbered order does not already say.
 *
 * The five steps run in order, and inside a stage the readings are listed in
 * the order somebody reads them — neither of those needs an arrow drawn on it.
 * What does is the handful of places where one screen's rows become another
 * screen's rows, and every one of these is a link the database already holds:
 * a gap knows which draft it became, a citation knows which page it points at,
 * a reflow report is the same question set asked again.
 *
 * Typed against the slugs rather than written as strings, so a reading renamed
 * in the table above takes its edges with it or fails to compile.
 */
export const EDGES: readonly (readonly [ReadingSlug, ReadingSlug])[] = [
	// A rival's answer is where a link we do not own was spent, and the page
	// that would have won it is ours to write.
	["citations", "published"],
	["gaps", "briefs"],
	["drafts", "published"],
	["published", "since"],
	// The edge that makes this a loop rather than a pipeline: what a published
	// page changed is measured by asking the same questions again.
	["since", "visibility"],
];
